import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import DatePicker from "react-datepicker";
import { toast } from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useSelector } from "react-redux";
import CustomDropdown from "../../../CustomControls/dropdownr";
import { Endpoints } from "../../../Global/Endpoints";
import { useTranslation } from 'react-i18next';

// Dropdown options
const reminderTypeOptions = [
  "Doctor's Appointment",
  "Receive Medication",
  "Appointment Reminder",
  "Log Event Reminder",
  "Check Personal Symptoms Reminder",
  "Other"
];

const remindMeOptions = [
  { label: "On Time", value: 0 },
  { label: "5 Minutes", value: 5 },
  { label: "10 Minutes", value: 10 },
  { label: "15 Minutes", value: 15 },
  { label: "30 Minutes", value: 30 },
  { label: "45 Minutes", value: 45 },
  { label: "1 Hour", value: 60 },
  { label: "1 Day", value: 1440 }
];

const repeatOptions = [
  { label: "Never", value: 0 },
  { label: "Every Day", value: 1 },
  { label: "Every Week", value: 7 },
  { label: "Every 2 Weeks", value: 14 },
  { label: "Every Month", value: 30 },
  { label: "Every Year", value: 365 }
];

Modal.setAppElement("#root");

const RemainderModal = ({ isOpen, onClose, selectedDate, reminderId, reminderDate, isEdit }) => {
  const { t, i18n } = useTranslation();
  const [remainderTitle, setRemainderTitle] = useState("");
  const [reminderType, setReminderType] = useState("");
  const [startDate, setStartDate] = useState(new Date()); // Default to current date
  const [endDate, setEndDate] = useState(new Date(new Date().getTime() + 30 * 60000)); // Default end date 30 mins later
  const [isAllDay, setIsAllDay] = useState("No");
  const [remindMe, setRemindMe] = useState("");
  const [repeat, setRepeat] = useState("");
  const [sendEmail, setSendEmail] = useState("N");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const deviceType = "PatientPortal";
  const [loading, setLoading] = useState(false);
  // Error states
  const [errors, setErrors] = useState({
    remainderTitle: "",
    reminderType: "",
    startDate: "",
    endDate: "",
    remindMe: "",
    repeat: "",
    notes: ""
  });

  const token = localStorage.getItem("AccessToken");
  const email = useSelector((state) => state.userData.userData.EmailID);
  useEffect(() => {
    if (isOpen) {
      const currentDate = new Date();
      
      if (isEdit && reminderId) {
        fetchReminderDetails(reminderId, reminderDate);
      } else if (selectedDate) {
        const date = new Date(selectedDate);
        // Check if the selected date is today
        if (date.toDateString() === currentDate.toDateString()) {
          // If selected date is today, set start and end dates to the current time
          setStartDate(currentDate);
          setEndDate(new Date(currentDate.getTime() + 30 * 60000)); // End date 30 mins later
        } else {
          setStartDate(date);
          setEndDate(new Date(date.getTime() + 30 * 60000)); // End date 30 mins later
        }
      } else {
        // If no selected date, initialize to current date and time
        setStartDate(currentDate);
        setEndDate(new Date(currentDate.getTime() + 30 * 60000)); // End date 30 mins later
      }
    }
  }, [isOpen, selectedDate, isEdit, reminderId]);

  const fetchReminderDetails = async (reminderId, reminderDate) => {
    const params = {
      ReminderID: reminderId,
      Date: reminderDate,
      EmailID: email,
      DeviceType: deviceType
    };

    try {
      const response = await axios.post(
        Endpoints.LoadReminderData,
        `_Params=${encodeURIComponent(JSON.stringify(params))}`,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data) {
        const reminderData = response.data;
        setRemainderTitle(reminderData.ReminderTitle || "");
        setReminderType(reminderData.ReminderType || "");
        setNotes(reminderData.TextOfReminder || "");
        setStartDate(new Date(reminderData.StartTime));
        setEndDate(new Date(reminderData.EndTime)); // Ensure end date is set correctly
        setRemindMe(reminderData.RemindBefore ?? "");
        setRepeat(reminderData.Repeat || "");
        setSendEmail(reminderData.ReminderbyMail === "Y" ? "Y" : "N");
        setIsAllDay(reminderData.AllDay === "Yes" ? "Yes" : "No");
      }
    } catch (error) {
      console.error("Error fetching reminder data:", error);
      toast.error("Failed to load reminder details.");
    }
  };

  const getFormattedDate = () => {
    const date = new Date(startDate);
    const options = { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const handleAllDayToggle = () => {
    const newAllDayValue = isAllDay === "No" ? "Yes" : "No";
    setIsAllDay(newAllDayValue);

    if (newAllDayValue === "Yes") {
      const startOfDay = new Date(startDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(startDate);
      endOfDay.setHours(23, 59, 59, 999);
      setStartDate(startOfDay);
      setEndDate(endOfDay);
    } else {
      const currentTime = new Date();
      const updatedStartDate = new Date(startDate);
      updatedStartDate.setHours(currentTime.getHours(), currentTime.getMinutes());
      setStartDate(updatedStartDate);
      setEndDate(new Date(updatedStartDate.getTime() + 30 * 60000)); // Set end date to 30 mins after start
    }
  };

  const resetFields = () => {
    const currentDate = new Date(); // Current date and time
    setRemainderTitle("");
    setReminderType("");
    setStartDate(currentDate); // Reset to current date
    setEndDate(new Date(currentDate.getTime() + 30 * 60000)); // Default end date 30 mins later
    setIsAllDay("No");
    setRemindMe("");
    setRepeat("");
    setSendEmail("N");
    setNotes("");
    setErrors({
      remainderTitle: "",
      reminderType: "",
      startDate: "",
      endDate: "",
      remindMe: "",
      repeat: "",
      notes: ""
    });
  };

  const handleSetRemainder = async () => {
    setErrors({
      remainderTitle: "",
      reminderType: "",
      startDate: "",
      endDate: "",
      remindMe: "",
      repeat: "",
      notes: ""
    });

    let hasError = false;
    if (!remainderTitle) {
      setErrors(prev => ({ ...prev, remainderTitle: t("Titlevalidation") }));
      hasError = true;
    }
    if (!reminderType) {
      setErrors(prev => ({ ...prev, reminderType: t("ReminderTypeValidation") }));
      hasError = true;
    }
    if (!startDate) {
      setErrors(prev => ({ ...prev, startDate: t("Starttimevalidation") }));
      hasError = true;
    }
    if (!endDate || endDate <= startDate) {
      setErrors(prev => ({ ...prev, endDate: t("endtimevalidation")}));
      hasError = true;
    }
    if (remindMe === "" || remindMe === null || remindMe === undefined) {
      setErrors((prev) => ({ ...prev, remindMe: t("remindertimevalidation") }));
      hasError = true;
    }
    
    // if (!remindMe) {
    //   setErrors(prev => ({ ...prev, remindMe: "Please select a reminder time." }));
    //   hasError = true;
    // }
    if (repeat === "" || repeat === null || repeat === undefined) {
      setErrors(prev => ({ ...prev, repeat: t("repeatoptionvalidation") }));
      hasError = true;
    }
    // if (!notes) {
    //   setErrors(prev => ({ ...prev, notes: t("notesvalidation")}));
    //   hasError = true;
    // }

    if (hasError) {
      return; // Stop execution if there are validation errors
    }

    setIsSubmitting(true);

    const utcStartDate = new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000);
    const utcEndDate = new Date(endDate.getTime() - endDate.getTimezoneOffset() * 60000);
    
    const payload = {
      RemindBefore: remindMe !== "" ? parseInt(remindMe) : null,
      //RemindBefore: parseInt(remindMe),
      SetLocation: "",
      ReminderbyMail: sendEmail,
      Repeat: repeat,
      StartTime: utcStartDate.toISOString().split(".")[0],
      ReminderDate: getFormattedDate(),
      AllDay: isAllDay,
      EmailID: email || "",
      EmailReminderTo: email || "",
      TextOfReminder: notes || "",
      EndTime: utcEndDate.toISOString().split(".")[0],
      ReminderTitle: remainderTitle,
      ReminderType: reminderType,
      ReminderID: isEdit ? reminderId : "",
      DeviceType: deviceType
    };
    setLoading(true);
    try {
      const response = await axios.post(
        Endpoints.SaveReminder,
        `_Params=${encodeURIComponent(JSON.stringify(payload))}`,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data?.Result === 1) {
      setTimeout(() => {
        toast.success(isEdit ? t("reminderupdatemessage") : t("remindersavemessage"));
      }, 500);
        resetFields();
      } else {
        toast.error(t("failedcase"));
      }
    } catch (error) {
      console.error("Error setting/updating reminder:", error);
      toast.error("An error occurred while setting/updating the reminder.");
    } finally {
      setIsSubmitting(false);
      setLoading(false);
      onClose();
    }
  };

  const handleChange = (setter, errorKey) => (value) => {
    setter(value);
    setErrors(prev => ({ ...prev, [errorKey]: "" }));
  };

  // Filter out past times
  const filterPastTimes = (time) => {
    const now = new Date();
    now.setSeconds(0, 0);
    return time > now; // Disable times that are in the past
  };

   if (loading) {
    return (
      <div className="loading-container">
        <div className="loading"></div>        
        <h2 className="loading-text">{t("loading")}</h2>
      </div>
    );
  }

  return (
    <div>
      <Modal
        isOpen={isOpen}
        onRequestClose={() => {
          resetFields();
          onClose();
        }}
        className="tracker-modal-content"
        overlayClassName="tracker-modal-overlay"
        shouldCloseOnOverlayClick={false}
      >
        <h2>{getFormattedDate()}</h2>

        <div className="model-form-wrapper">
          <div className="mb-4 row align-items-center">
            <label className="col-sm-4 model-form-label mandatory">{t("Remaindertitle")}</label>
            <div className="col-sm-6">
              <input
                type="text"
                className="form-control win-form-control-c mobile-view"
                placeholder={t("Remaindertitleplaceholder")}
                value={remainderTitle}
                onChange={(e) => handleChange(setRemainderTitle, "remainderTitle")(e.target.value)}
                maxLength={50}
              />
              {errors.remainderTitle && <small className="form-field-error-message">{errors.remainderTitle}</small>}
            </div>
          </div>

          <div className="mb-4 row align-items-center">
  <label className="col-sm-4 model-form-label mandatory">{t("RemainderType")}</label>
  <div className="col-sm-6 custom-dropdown-forced-380px-in-tab custom-dropdown-100pc-only-mobile">
  <CustomDropdown
      options={reminderTypeOptions} // Pass reminder type options
      selectedOption={reminderType} // Currently selected option
      onChange={(option) => handleChange(setReminderType, "reminderType")(option)} // Handle option change
      searchable={false} // Enable search
      placeholder={t("RemainderTypePlaceholder")} // Placeholder text
    />
    {errors.reminderType && <small className="form-field-error-message">{errors.reminderType}</small>}
  </div>
</div>


          <div className="mb-4 row align-items-center switch-control-wrapper">
            <label className="col-sm-4 model-form-label"></label>
            <div className="col-sm-6">
              <label className="switch">
                <input
                  type="checkbox"
                  className="switch-input"
                  checked={isAllDay === "Yes"}
                  onChange={handleAllDayToggle}
                />
                <span className="slider round"></span>
              </label>
              <span className="modelswitch-label ms-4">{t("AllDay")}</span>
            </div>
          </div>

          <div className="mb-4 row align-items-center">
            <label className="col-sm-4 model-form-label">{t("StartTime")}</label>
            <div className="col-sm-6">
              <DatePicker
                selected={startDate}
                onChange={(time) => {
                  if (time) {
                    setStartDate(time);
                    setEndDate(new Date(time.getTime() + 30 * 60000)); // Set end date 30 minutes after the start date
                  }
                }}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={30}
                timeFormat="hh:mm aa"
                dateFormat= "MM/dd/yyyy, hh:mm aa"
                className="form-control win-form-control-f calendar-control mobile-view"
                placeholderText={t("StartTimeplaceholder")}
                filterTime={filterPastTimes} // Filter out past times
                disabled={isAllDay === "Yes"}
                onKeyDown={(e) => e.preventDefault()}
              />
              {errors.startDate && <small className="form-field-error-message">{errors.startDate}</small>}
            </div>
          </div>

          <div className="mb-4 row align-items-center">
            <label className="col-sm-4 model-form-label">{t("EndTime")}</label>
            <div className="col-sm-6">
              <DatePicker
                selected={endDate}
                onChange={(time) => {
                  if (time) {
                    // Ensure end time is later than start time
                    if (time <= startDate) {
                      toast.error(t("toasterr"));
                      return; // Prevent setting invalid end time
                    }
                    setEndDate(time);
                  }
                }}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={30}
                timeFormat="hh:mm aa"
                dateFormat="MM/dd/yyyy, hh:mm aa"
                className="form-control win-form-control-f calendar-control mobile-view"
                placeholderText={t("EndTimeplaceholder")}
                filterTime={filterPastTimes} // Filter out past times
                disabled={isAllDay === "Yes"}
                onKeyDown={(e) => e.preventDefault()}
              />
              {errors.endDate && <small className="form-field-error-message">{errors.endDate}</small>}
            </div>
          </div>

          <div className="mb-4 row align-items-center">
            <label className="col-sm-4 model-form-label mandatory">{t("RemindMe")}</label>
            <div className="col-sm-6 custom-dropdown-forced-380px-in-tab custom-dropdown-100pc-only-mobile">
            <CustomDropdown
      options={remindMeOptions.map((option) => option.label)} // Extract labels for dropdown
      selectedOption={remindMeOptions.find((o) => o.value === remindMe)?.label || ""} // Map value to label
      onChange={(option) =>
        handleChange(setRemindMe, "remindMe")(remindMeOptions.find((o) => o.label === option)?.value)
      }
      searchable={true} // Enable search
      placeholder={t("RemindMeplaceholder")} // Placeholder text
    />
              {errors.remindMe && <small className="form-field-error-message">{errors.remindMe}</small>}
            </div>
          </div>

          <div className="mb-4 row align-items-center">
            <label className="col-sm-4 model-form-label mandatory">{t("Repeat")}</label>
            <div className="col-sm-6 custom-dropdown-forced-380px-in-tab custom-dropdown-100pc-only-mobile">
            <CustomDropdown
      options={repeatOptions.map((option) => option.label)} // Extract labels for dropdown
      selectedOption={
    repeatOptions.find((o) => o.value === parseInt(repeat))?.label || ""
  } // Map value to label
      onChange={(option) =>
        handleChange(setRepeat, "repeat")(repeatOptions.find((o) => o.label === option)?.value)
      }
      searchable={false}
      placeholder={t("Repeatplacholder")}
    />
              {errors.repeat && <small className="form-field-error-message">{errors.repeat}</small>}
            </div>
          </div>

          <div className="mb-4 row align-items-center switch-control-wrapper">
            <label className="col-sm-4 model-form-label"></label>
            <div className="col-sm-6">
              <label className="switch">
                <input
                  type="checkbox"
                  className="switch-input"
                  checked={sendEmail === "Y"}
                  onChange={() => setSendEmail(sendEmail === "Y" ? "N" : "Y")}
                />
                <span className="slider round"></span>
              </label>
              <span className="modelswitch-label ms-4">{t("Sendreminderbyemail")}</span>
            </div>
          </div>

          <div className="mb-4 row">
            <label className="col-sm-4 model-form-label mt-2">{t("Notes")}</label>
            <div className="col-sm-6">
              <textarea
                value={notes}
                onChange={(e) => handleChange(setNotes, "notes")(e.target.value)}
                placeholder={t("Notesplaceholder")}
                className="form-control win-form-control textarea-control mobile-view-important"
                rows="4"
                maxLength={100}
              />
              {errors.notes && <small className="form-field-error-message">{errors.notes}</small>}
            </div>
          </div>

          <div className="mb-4 row">
            <div className="col-sm-4"></div>
            <div className="col-sm-8">
              <button 
                onClick={() => {
                  resetFields(); 
                  onClose(); 
                }} 
                className="win-btn-secondary btn-w-180px model-popup-btn me-3 mb-3 mb-sm-0"
              >
                {t("Cancel")}
              </button>
              <button 
                onClick={handleSetRemainder} 
                className="win-btn-primary btn-w-180px model-popup-btn"
                disabled={isSubmitting}
              >
                {isEdit ? t("Update") : t("Save")}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RemainderModal;
